import {Component, ElementRef, signal, ViewChild} from "@angular/core";
import {Chart} from "chart.js";
import {EnergyReportsFacade} from "@store/energy-reports/energy-reports.facade";
import { environment } from '@environments/environment';
import {combineLatest, firstValueFrom, map} from "rxjs";
import {HomesFacade} from "@library/store/homes/homes.facade";
import {LocalStorageService} from "@library/utils/services/local-storage.service";
import {isDefined} from "@library/utils/helpers/store/is-defined";
import {ChangeDetectionStrategy} from "@angular/core";
import { getColorValue } from '@library/utils/helpers/functions/getColorVariable';

@Component({
  selector: 'app-cp-reports-comparison-with-other-homes-card',
  templateUrl: './cp-reports-comparison-with-other-homes-card.component.html',
  styleUrls: ['./cp-reports-comparison-with-other-homes-card.component.scss'],
  changeDetection:  ChangeDetectionStrategy.OnPush
})
export class CpReportsComparisonWithOtherHomesCardComponent {
  chart: Chart;
  width: number;
  height: number;
  gradient: CanvasGradient;

  report$ = this.energyReportsFacade.currentReport$.pipe(isDefined());

  viewModel$ = combineLatest({
    report: this.report$,
    home: this.homesFacade.currentHome$,
  });

  @ViewChild('MyChart') el: ElementRef;

  previousImage = {
    xPixel: 0,
    yPixel: 0,
  };

  showModalSettings = signal(false);

  settingsLegrandUrl$ = this.homesFacade.currentHomeId$.pipe(
    isDefined(),
    map((id) => {
      const theme = this.localStorage.getItem('theme');
      return `${environment.settingsLegrandUrl}home/${id}?data_theme=${theme}`;
    })
  );

  constructor(
    private homesFacade: HomesFacade,
    private energyReportsFacade: EnergyReportsFacade,
    private localStorage: LocalStorageService
  ) {
  }

  afterDraw(chart: Chart, index: number, data: {x: number, y: number}[], tbo_50_percent :number, tbo_75_percent :number) {

    const biggestY = data.reduce((prev, current) => (prev.y > current.y) ? prev : current);

    const color = {
      green : '--green-confirmation',
      orange : '--light-orange',
      red : '--red-alert'
    };
    const image = {
      green : "assets/images/control-page/reports/location/LocalisationGreen.svg",
      orange : "assets/images/control-page/reports/location/LocalisationOrange.svg",
      red : "assets/images/control-page/reports/location/LocalisationRed.svg"
    };

    const getImageByValue = (value) => {
      if (value < tbo_50_percent) {
        return image.green;
      }
      else if (value < tbo_75_percent) {
        return image.orange;
      }
      else {
        return image.red;
      }
    };

    const getColorByValue = (value) => {
      if (value < tbo_50_percent) {
        return color.green;
      }
      else if (value < tbo_75_percent) {
        return color.orange;
      }
      else {
        return color.red;
      }
    };

    const chartArea = chart.chartArea;
    const xPixel = chart.scales.x.getPixelForValue(index);
    const startXPixel = chart.scales.x.getPixelForValue(0);

    if (startXPixel === xPixel) {
      return;
    }

    const {ctx} = chart;

    const yPixel = chart.scales.y.getPixelForValue(data[index].y);

    const biggestYPixel = chart.scales.y.getPixelForValue(biggestY.y);

    const yPixelDiff = biggestYPixel - yPixel;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(xPixel, chartArea.bottom);
    ctx.lineTo(xPixel, yPixel - yPixelDiff / 2 * -1.2);
    ctx.lineWidth = 3;
    ctx.strokeStyle = getComputedStyle(document.documentElement)
      .getPropertyValue(getColorByValue(data[index].x));
    ctx.stroke();

    const img = new Image();

    img.src = getImageByValue(data[index].x);

    img.onload = () => {
      ctx.drawImage(img, xPixel - img.width / 2, yPixel - yPixelDiff / 2 * -1.2 - img.height - 10);
    };

    ctx.restore();
  }

  ngAfterViewInit() {
    this.viewModel$.subscribe(() => {
      this.createChart();

    });
  }

  async createChart(){

    if (this.chart) {
      this.chart.destroy();
      this.gradient = null;
      // clear the canvas
      const canvas = this.el.nativeElement;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const report = await firstValueFrom(this.report$);

    if (!report?.heating_time_compared_to_similar_houses_graph_data?.heating_time_similar_houses_points) {
      return;
    }

    const {tbo_50_percent, tbo_75_percent} = report.heating_time_compared_to_similar_houses_graph_data;

    const similar_points = [{"x" : 0, "y" : 0}, ...report.heating_time_compared_to_similar_houses_graph_data.heating_time_similar_houses_points];

    const percentage  = report.heating_time_compared_to_similar_houses_in_percentage;

    const {total_heating_time} = report;

    let cumulativePercentage = 0;
    let indexToPercentage = 0;

    for (const point of similar_points) {
      cumulativePercentage += point.y;

      if (cumulativePercentage >= percentage) {
        break;
      }
      else {
        indexToPercentage++;
      }
    }

    const lastPoint = similar_points[similar_points.length - 1].x;

    this.chart = new Chart(
      this.el.nativeElement
      ,  {
        type: 'line',
        data: {
          labels : similar_points.map((d) => d.x),
          datasets: [
            {
              data: similar_points.map((d) => d.y),
              backgroundColor: (context) => {
                const chart = context.chart;
                const {ctx, chartArea} = chart;
                if (!chartArea) {
                  return null;
                }
                return this.getGradient(ctx, chartArea, 'background',tbo_50_percent, tbo_75_percent, lastPoint);
              },
              borderColor: (context) => {
                const chart = context.chart;
                const {ctx, chartArea} = chart;
                if (!chartArea) {
                  return null;
                }
                return this.getGradient(ctx, chartArea, 'border', tbo_50_percent, tbo_75_percent, lastPoint);
              },
              borderWidth: 3,
              fill : true,
              tension: 0.4,
            },
          ]
        },
        options: {
          hover : {
            mode : null
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              grid : {
                display : false,
              },
              ticks: {
                callback: function(value, index, values) {

                  if (value === indexToPercentage) {
                    return Math.round(total_heating_time / 3600) + ' h';
                  }
                  else if (index === 0 && indexToPercentage > 3) {
                    return Math.round(similar_points[index].x) + ' h';
                  }
                  else if (index === values.length - 1  && indexToPercentage < values.length - 3) {
                    return '+ ' + Math.round(similar_points[index].x) + ' h';
                  }
                  else {
                    return null;
                  }
                },
                font : {
                  size : 16,
                  family : "ProximaNova-Semibold",
                },
                color : getColorValue("--grey-70")
              }
            },
            y: {
              ticks: {
                display: false // hide Y-axis labels
              },
              grid: {
                display: false,
                borderColor : getColorValue("--grey-20")
              }
            }
          },
          plugins: {
            filler : {
              propagate: true
            },
            tooltip : {
              enabled : false
            },
            legend: {
              display: false, // hide legend
            },
          },
          elements: {
            point:{
              radius: 0
            }
          },
          animation: {
            onComplete: ({chart}) => {
              this.afterDraw(chart, indexToPercentage, similar_points, tbo_50_percent, tbo_75_percent);
            }
          },
          layout: {
            padding : {
              top : 64
            }
          }
        },
      });

  }

  getGradient(ctx: CanvasRenderingContext2D, chartArea: { top: number, bottom: number, left: number, right: number }, type: 'background' | 'border', tbo_50_percent: number, tbo_75_percent: number, lastPoint : number) {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!this.gradient || this.width !== chartWidth || this.height !== chartHeight) {
      // Create the gradient because this is either the first render
      // or the size of the chart has changed
      this.width = chartWidth;
      this.height = chartHeight;

      this.gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
      // Create a horizontal gradient// 50% opacity
      this.gradient.addColorStop(tbo_50_percent / lastPoint, `rgb(148, 230, 141)`); // 50% opacity
      this.gradient.addColorStop(tbo_75_percent / lastPoint, `rgb(255, 217, 144)`);// 50% opacity
      this.gradient.addColorStop(1, `rgb(255, 121, 121)`);// 50% opacity
      // 50% opacity
    }

    return this.gradient;
  }

  handleShowModalSettings() {
    this.showModalSettings.set(true);
  }
  handleCloseModalSettings() {
    this.showModalSettings.set(false);
  }

  getPercentageRound(percentage: number) {
    return Math.round(percentage).toLocaleString();
  }

}
